import type { ComponentProps, JSX, ReactNode } from 'react'

import { useReadOnly } from '../components/read-only.tsx'
import { cn } from '../lib/cn.ts'

/**
 * Square, ink accent, full opacity when disabled. The browser's own fade
 * would drop the label below the contrast floor.
 */
const BOX_CLASS =
  'size-4 shrink-0 rounded-none accent-foreground disabled:bg-sunk disabled:text-muted-foreground disabled:opacity-100'

type CheckboxBase = Omit<ComponentProps<'input'>, 'type'> & {
  'data-testid': string
  /** Classes on the visible label. Unused when the box has only an aria-label. */
  labelClassName?: string
}

/**
 * A visible label, or an aria-label for a box that sits in a table cell.
 * A box with neither has no accessible name.
 */
export type CheckboxProps = CheckboxBase &
  ({ label: ReactNode } | { label?: undefined; 'aria-label': string })

/**
 * Checkbox — a native checkbox.
 *
 * Native, not a painted stand-in, so `indeterminate` still works on the page
 * box. The accent is ink, never the brand magenta.
 *
 * @param props - Checkbox props. `data-testid` is required.
 * @param props.label - Visible label. Omit it only for a table cell.
 * @param props.labelClassName - Classes on the label element.
 * @param props.className - Classes on the input. They win over the house box.
 * @returns The box, wrapped in a label when `label` is set, or nothing on a read-only screen.
 */
export function Checkbox({
  label,
  labelClassName,
  className,
  'data-testid': testId,
  ...props
}: CheckboxProps): JSX.Element | null {
  const readOnly = useReadOnly()
  if (readOnly) return null
  const input = (
    <input
      type="checkbox"
      data-testid={testId}
      className={cn(BOX_CLASS, className)}
      {...props}
    />
  )
  if (label === undefined) return input
  return (
    <label className={cn('inline-flex items-center gap-2', labelClassName)}>
      {input}
      {label}
    </label>
  )
}
